<template>
  <hl-Modal dismisable @close="toggleCreditNoteModal(false)">
    <!-- Loader -->
    <div v-if="loading.order" class="text-center py-5">
      <div class="spinner-grow text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <!-- / Loader -->
    <div v-else class="text-left">
      <div>
        <h3 class="text-center">Opprett kreditnota</h3>

        <InfoCard class="mt-4">
          For å kreditere en faktura fyller du ut info i feltene under. Skal leietaker ha tilsendt kopi så husk å merke
          “Ja” for dette. Etter du har klikket “Opprett” vil opprinnelig faktura krediteres (reduseres) med det valgte
          krediteringsbeløpet.
        </InfoCard>

        <div class="row align-items-center mt-5">
          <div class="col">Kundenummer</div>
          <div class="col">
            <span v-if="order">{{ order.customerId }}</span>
          </div>

          <div class="col">Send til leietaker?</div>
          <div class="col">
            <hl-Switch v-model="creditNote.distributeToTenant" onState="Ja" offState="Nei" class="ml-3" showState />
          </div>
        </div>

        <div class="row align-items-center mt-2">
          <div class="col-3">Forfalls-/Fakturadato</div>
          <div class="col-4 font-weight-bold">
            <hl-Datepicker v-model="creditNote.dueDate"></hl-Datepicker>
            <!--<hl-Date :date="order.dueDate"></hl-Date>-->
          </div>
        </div>
      </div>

      <div class="bg-light mt-5 pb-5 bg-absolute">
        <div class="info">
          Opprinnelig fakturabeløp er <hl-Currency :amount="order.amount.invoiced" />. Du bør maksimalt kreditere
          <hl-Currency :amount="order.amount.unpaid" />
        </div>
        <div class="table-responsive mt-3">
          <table class="ledger-table table">
            <thead>
              <tr>
                <th scope="col">Beskrivelse</th>
                <th scope="col">MVA</th>
                <th scope="col">Krediteringsbeløp eks. MVA</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in creditNote.rows" :key="row.id">
                <td scope="row">
                  <hl-Input size="sm" v-model="row.text" placeholder="skriv inn beskrivelse"></hl-Input>
                </td>
                <td style="width: 100px">
                  <hl-Select
                    v-if="row.isNew"
                    :hasMarginBottom="false"
                    size="sm"
                    v-model="row.vat"
                    :options="MVAOptions"
                    :disabled="MVAOptions.length <= 1"
                  />
                  <hl-Select
                    v-else
                    :hasMarginBottom="false"
                    size="sm"
                    v-model="row.vat"
                    :options="rowVat(row)"
                    :disabled="true"
                  />
                </td>
                <td>
                  <hl-Input type="number" size="sm" v-model="row.amount" />
                </td>

                <td>
                  <button class="btn btn-sm remove-row">
                    <i @click="removeRow(row.id)" class="budicon-trash"></i>
                  </button>
                </td>
              </tr>

              <tr>
                <td colspan="4">
                  <div class="add-row" @click="addRow">Legg til linje</div>
                </td>
              </tr>

              <tr class="saldo">
                <td>Sum uten MVA (Nettobeløp)</td>
                <td></td>
                <td class="text-right">
                  <hl-Currency :amount="calculatedSumWithoutVAT"></hl-Currency>
                </td>
              </tr>

              <tr>
                <td>Sum MVA (Merverdiavgift)</td>
                <td></td>
                <td class="text-right">
                  <hl-Currency :amount="calculatedSumVAT"></hl-Currency>
                </td>
              </tr>
            </tbody>
            <tbody>
              <tr>
                <th>Krediteres</th>
                <td></td>
                <th class="text-right">
                  <hl-Currency :amount="totalSum"></hl-Currency>
                </th>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-4 text-center">
          <hl-Button variant="primary" @click="postCreditNote" :loading="loading.posting">Krediter</hl-Button>
        </div>
      </div>
    </div>
  </hl-Modal>
</template>

<script>
import canLandlordChargeVAT from '@/mixins/tenancy/canLandlordChargeVAT';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Datepicker from '@modules/husleie-framework/src/components/forms/inputs/Datepicker.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import Switch from '@modules/husleie-framework/src/components/forms/inputs/switch/Switch.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import InfoCard from '@/components/InfoCard.vue';
import * as moment from 'moment';
import { mapGetters, mapActions, mapMutations } from 'vuex';

function createCreditRowsFromOrder(order) {
  if (order.amount.unpaid === order.amount.invoiced) {
    return JSON.parse(JSON.stringify(order.rows));
  }
  const rows = JSON.parse(JSON.stringify(order.rows));
  let toCredit = order.amount.unpaid;
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.amount <= toCredit) {
      toCredit -= row.amount;
    } else {
      row.amount = toCredit;
      toCredit = 0;
    }
  }
  return rows;
}

export { createCreditRowsFromOrder };

export default {
  components: {
    InfoCard,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Input': Input,
    'hl-Select': Select,
    'hl-Currency': Currency,
    'hl-Datepicker': Datepicker,
    'hl-Switch': Switch,
  },

  props: {
    paymentId: {
      type: Number,
    },
    landlordId: {
      type: [Number, String],
    },
    tenancyId: {
      type: [Number, String],
    },
    orderId: {
      type: [Number, String],
    },
  },

  mixins: [canLandlordChargeVAT],

  data() {
    return {
      creditNote: {
        invoiceType: 'additional',
        dueDate: null,
        dueDateType: 'today',
        distributeToTenant: true,
        rows: [],
      },
      order: null,
      loading: {
        order: false,
        posting: false,
      },
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancyDetailedInvoice']),

    today() {
      return moment().toDate();
    },

    invoiceTypeOptions() {
      return [
        { name: 'Tilleggsfaktura', value: 'additional' },
        { name: 'Husleiefaktura', value: 'rent' },
      ];
    },

    calculatedSumWithoutVAT() {
      if (this.creditNote.rows == null) return;
      const sum = this.creditNote.rows.reduce((acc, row) => {
        return acc + parseInt(row.amount);
      }, 0);
      return sum;
    },

    calculatedSumVAT() {
      if (this.creditNote.rows == null) return;
      const sum = this.creditNote.rows.reduce((acc, row) => {
        return acc + parseInt(row.amount * row.vat) / 100;
      }, 0);
      return sum;
    },

    totalSum() {
      return this.calculatedSumWithoutVAT + this.calculatedSumVAT;
    },

    totalOrderInvoiced() {
      if (this.order == null) return;
      return this.order.amount.invoiced;
    },

    totalRemaining() {
      if (this.order == null) return;
      return this.order.amount.unpaid - this.totalSum;
    },
  },

  methods: {
    ...mapMutations('tenancy', ['toggleCreditNoteModal']),
    ...mapActions('tenancy', ['fetchTenancyOrder', 'postTenancyAccountingOrder']),

    async fetchOrder() {
      try {
        const order = await this.fetchTenancyOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.orderId,
        });
        return order;
      } catch (err) {
        console.error(err);
        this.$toast('Kunne ikke hente ordre.', 'error');
      }
    },

    addRow() {
      this.creditNote.rows.push({
        id: this.creditNote.rows.length,
        text: '',
        vat: 0,
        amount: null,
        isNew: true,
      });
    },

    rowVat(row) {
      return this.MVAOptionForRow(row);
    },

    removeRow(id) {
      const index = this.creditNote.rows.findIndex(row => {
        return row.id === id;
      });

      this.creditNote.rows.splice(index, 1);
    },

    getPaymentInfo() {
      if (this.paymentId == null) return;
    },

    async postCreditNote() {
      // IMPORTANT change dueDate
      this.loading.posting = true;
      const order = {
        creditNotedOrderId: this.order.id,
        dueDate: moment(this.creditNote.dueDate).format('YYYY-MM-DD'),
        period: this.order.period,
        rows: this.creditNote.rows,
        sendCopy: this.creditNote.distributeToTenant,
        typeOf: 'creditNote',
      };
      try {
        await this.postTenancyAccountingOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: order,
        });
        this.$emit('newCreditNote');
        this.toggleCreditNoteModal(false);
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre ordre.', 'error');
      }
      this.loading.posting = false;
    },

    createCreditRowsFromOrder,
  },

  async created() {
    this.loading.order = true;

    this.order = await this.fetchOrder();
    this.creditNote.rows = this.createCreditRowsFromOrder(this.order);
    this.creditNote.dueDate = this.today;

    this.loading.order = false;
  },

  beforeDestroy() {
    // ensure that we destroy an unsaved invoice
    this.newInvoice = {};
  },
};
</script>

<style scoped lang="scss">
.bg-absolute {
  margin-left: -3rem;
  margin-bottom: -3rem;
  margin-top: -3rem;
  width: calc(100% + 6rem);
  padding: 3rem;
}

.add-row {
  width: 100%;
  padding: 1rem;
  background: $gray-100;
  text-align: center;
  border: 1px solid $gray-300;
  border-radius: $border-radius-medium;
  color: $gray-800;
  box-shadow: $box-shadow-1;
  cursor: pointer;
}

.remove-row {
  background: white;
  border: 1px solid $gray-400;
  height: 31px;

  i {
    font-size: 1rem !important;
  }
}

hr {
  border-bottom: 1px solid $gray-500;
  margin: 0.4rem 15px;
}

.info {
  background: white;
  padding: 1rem;
  border: 2px solid $warning;
  word-break: break-all;
}
</style>
